import React, { FC, useEffect, useState } from 'react';
import { Info } from '@nexus/lib-react/dist/core/Icon/icons/default';
import { Paragraph4 } from '@nexus/lib-react/dist/core/Typography/Paragraph';
import { getConfig } from '@olxeu-a2b/delivery-utils/src/extras/getConfig';
import useI18n from '../../i18n/hooks/useI18n';
import styled from '../../theme/styled';

const InfoIcon = styled(Info)`
  height: ${({ theme }) => theme.sizes[16]};
  width: ${({ theme }) => theme.sizes[16]};
  color: ${({ theme }) => theme.colors['text-global-secondary']};
`;

const HintText = styled(Paragraph4)`
  margin-left: ${({ theme }) => theme.space[8]};
  font-size: ${({ theme }) => theme.fontSizes[12]};
  color: ${({ theme }) => theme.colors['text-global-secondary']};
`;

const HintAnchor = styled.a`
  color: ${({ theme }) => theme.colors['text-global-secondary']};
`;

const HintWrapper = styled.div`
  display: flex;
`;

const HintIconContainer = styled.div`
  display: flex;
`;

interface HintProps {
  hintKey: string;
  returnId?: string;
  timeToNegotiate?: number;
  actionDeadline?: string;
  postingDeadlineStart?: string;
  postingDeadlineEnd?: string;
}

const Hint: FC<HintProps> = ({
  hintKey,
  returnId,
  timeToNegotiate,
  actionDeadline,
  postingDeadlineStart,
  postingDeadlineEnd,
}) => {
  const i18n = useI18n();
  const config = getConfig();
  const redirectUrl = returnId ? `${config?.sites?.desktop?.url}/orders/returns/${returnId}` : '';
  const [dayNumber, setDayNumber] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  const addLeadingZero = (value: number) => (value.toString().length === 1 ? `0${value}` : value);
  const formatDeadline = (deadline: string, endDeadline?: string) => {
    const initialDate = new Date(deadline);
    setDayNumber(initialDate.getDate().toString());
    const dayNum = initialDate.getDay().toString();
    const monthNum = initialDate.getMonth().toString();
    setDay(i18n(`dlv_returns_day_${dayNum}`));
    setMonth(i18n(`dlv_returns_month_${monthNum}`));
    const hour = () => addLeadingZero(initialDate.getHours());
    const minutes = () => addLeadingZero(initialDate.getMinutes());
    setTime(`${hour()}:${minutes()}`);

    if (endDeadline) {
      const postingDeadlineEndDate = new Date(endDeadline);
      const endHour = () => addLeadingZero(postingDeadlineEndDate.getHours());
      const endMinutes = () => addLeadingZero(postingDeadlineEndDate.getMinutes());
      setEndTime(`${endHour()}:${endMinutes()}`);
    }
  };

  useEffect(
    () =>
      postingDeadlineStart && postingDeadlineEnd
        ? formatDeadline(postingDeadlineStart, postingDeadlineEnd)
        : formatDeadline(actionDeadline),
    [postingDeadlineStart, postingDeadlineEnd, actionDeadline],
  );

  const keyNameWithUrl = 'dlv_returns_status_info_seller_delivery_posted';
  const keyNameWithUrlLink = i18n('dlv_returns_status_info_seller_delivery_posted_link');
  const link = (
    <HintAnchor href={redirectUrl} dangerouslySetInnerHTML={{ __html: keyNameWithUrlLink }} />
  );

  return (
    <HintWrapper data-testid="hint">
      <HintIconContainer data-testid="info-icon">
        <InfoIcon />
      </HintIconContainer>
      {hintKey === keyNameWithUrl ? (
        <HintText data-testid="info-text">
          {i18n`${hintKey}`} {link}
        </HintText>
      ) : (
        <HintText
          data-testid="info-text"
          dangerouslySetInnerHTML={{
            __html: i18n(hintKey, '', {
              day,
              dayNumber,
              month,
              time,
              endTime,
              timeToNegotiate,
            }),
          }}
        />
      )}
    </HintWrapper>
  );
};

export default Hint;
